import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Person picker — pick an infras-app USER to assign as Owner/Maintainer. Same
 * portal/flip mechanics as ProductLineCombo so it can't be clipped by the table's
 * overflow. Assignable people are constrained to users the app knows (they must
 * have signed in / been granted a role); there is no free-text entry.
 */
export interface PersonOption {
  email: string;
  label: string; // display name (falls back to email)
}

const PANEL_W = 260;
const PANEL_MAX = 320;
const GAP = 4;
interface Placement { left: number; top: number; up: boolean; maxHeight: number }
function place(rect: DOMRect): Placement {
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_W - 8));
  const below = window.innerHeight - rect.bottom;
  const above = rect.top;
  const up = below < PANEL_MAX && above > below;
  return { left, top: up ? rect.top - GAP : rect.bottom + GAP, up, maxHeight: Math.min(PANEL_MAX, (up ? above : below) - 8) };
}

export function PersonCombo({
  placeholder = 'assign…',
  options,
  disabled,
  onPick,
  id,
  ariaLabel,
}: {
  /** Optional: lets a form <label htmlFor> / aria-label name the trigger. */
  id?: string;
  ariaLabel?: string;
  placeholder?: string;
  options: PersonOption[];
  disabled?: boolean;
  onPick: (email: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const reposition = () => {
    if (triggerRef.current) setPos(place(triggerRef.current.getBoundingClientRect()));
  };
  useLayoutEffect(() => {
    if (open) reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // The panel only renders once it has a position, so focus after `pos` lands.
  useEffect(() => {
    if (open && pos) searchRef.current?.focus({ preventScroll: true });
  }, [open, pos !== null]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const onMove = () => reposition();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('mousedown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ql = q.trim().toLowerCase();
  const filtered = options.filter((o) => o.label.toLowerCase().includes(ql) || o.email.toLowerCase().includes(ql));
  const pick = (email: string) => {
    onPick(email);
    setOpen(false);
    setQ('');
  };

  const panel =
    open && pos
      ? createPortal(
          <div
            ref={panelRef}
            className="pl-combo-panel"
            style={{ position: 'fixed', left: pos.left, top: pos.top, width: PANEL_W, maxHeight: pos.maxHeight, transform: pos.up ? 'translateY(-100%)' : undefined, zIndex: 1000 }}
          >
            <input
              ref={searchRef}
              className="pl-combo-search"
              placeholder="filter people…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
              }}
            />
            <div className="pl-combo-list">
              {filtered.map((o) => (
                <button type="button" key={o.email} className="pl-combo-item" onClick={() => pick(o.email)}>
                  <span>{o.label}</span>
                  {o.label !== o.email && <span className="muted text-2xs" style={{ marginLeft: 6 }}>{o.email}</span>}
                </button>
              ))}
              {!filtered.length && <div className="pl-combo-empty muted">no matching app users</div>}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="pl-combo">
      <button ref={triggerRef} id={id} aria-label={ariaLabel} aria-expanded={open} type="button" className="pl-combo-trigger" disabled={disabled} onClick={() => setOpen((o) => !o)}>
        <span className="muted">{placeholder}</span>
        <span className="pl-combo-caret" aria-hidden>▾</span>
      </button>
      {panel}
    </div>
  );
}
